// Stefans-AI-CV-frontend/src/sections/Experience.tsx

import { motion } from 'framer-motion'
import { useI18n } from '../i18n'

export default function Experience() {
  const t = useI18n()

  return (
    <section id="experience" className="py-24 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-cyan-400 text-sm font-mono mb-2">// experience</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-3">
            {t.experience.title}
          </h2>
          <p className="text-text-secondary">{t.experience.subtitle}</p>
        </motion.div>

        {/* Pre-web3 note */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="mb-10 pl-4 border-l-2 border-dashed border-border"
        >
          <p className="text-text-muted text-sm font-mono">{t.experience.pre_web3}</p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-4 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-400/40 via-indigo-400/20 to-transparent hidden md:block" />

          <div className="space-y-8">
            {t.experience.items.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative md:pl-20"
              >
                {/* Year badge on line */}
                <div className="hidden md:flex absolute left-0 top-1.5 items-center">
                  <div className="w-4 h-4 rounded-full bg-cyan-400/20 border-2 border-cyan-400/60 relative z-10 ml-6" />
                </div>

                {/* Card */}
                <div className="glass-card rounded-2xl p-6 hover:border-border-strong transition-all duration-300 group">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs font-semibold text-cyan-400 bg-cyan-dim px-2 py-0.5 rounded-md">
                          {item.year}
                        </span>
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-text-primary group-hover:text-cyan-400 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-text-muted text-sm mt-0.5">{item.company}</p>
                    </div>
                  </div>

                  <p className="text-text-secondary text-sm leading-relaxed mb-3">{item.desc}</p>

                  {/* Key bullets */}
                  {(item as { bullets?: string[] }).bullets && (
                    <ul className="mb-4 space-y-1.5">
                      {((item as { bullets: string[] }).bullets).map((b: string, j: number) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-text-secondary">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-cyan-400/60 flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag: string, j: number) => (
                      <span
                        key={j}
                        className="px-2.5 py-1 text-xs rounded-lg bg-white/5 text-text-muted border border-border hover:border-border-strong transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
